import clsx from 'clsx'
import noop from 'lodash/noop'
import React, { FC, ReactNode, useCallback, useEffect } from 'react'
import ReactFocusLock from 'react-focus-lock'
import { useKeyPressEvent } from 'react-use'
import styled, { css } from 'styled-components'

import SVGAngleRight from '@Assets/SVG/AngleRight'
import SVGClose from '@Assets/SVG/Close'
import { Backdrop } from '@Components/Backdrop'
import { StyledSVGIcon, SVGIcon } from '@Components/Icons'
import { enableBodyScroll, preventBodyScroll } from '@Globals/BodyHelpers'
import { up } from '@Globals/Style/breakpoints'
import { box, resetButton, smoothScroll } from '@Globals/Style/mixins'
import appTheme from '@Globals/Style/theme'
import { useTranslation } from '@Globals/Translations'
import { useMediaQuery } from '@Globals/useMediaQuery'

type SidePaneProps = {
  children: ReactNode
  footerContent?: ReactNode
  fullScreenOnMobile?: boolean
  isShowing: boolean
  maxWidth?: string
  onClose: () => void
  showCloseButton?: boolean
  side?: 'left' | 'right'
  title?: ReactNode
  useBackdrop?: boolean
}

const SidePaneWrapper = styled.aside<Pick<SidePaneProps, 'isShowing' | 'side' | 'fullScreenOnMobile' | 'maxWidth'>>(({
  fullScreenOnMobile,
  isShowing,
  maxWidth,
  side,
  theme,
}) => css`
  position: fixed;
  top: 0;
  z-index: ${theme.zIndexes.globalSidePane};
  overflow: auto;
  ${box({ height: '100%', width: fullScreenOnMobile ? '100%' : '80vw' })};
  ${side === 'left' ? 'max-width: 320px' : ''};
  background-color: ${theme.colors.white};
  box-shadow: ${isShowing ? '0 0 8px rgba(0, 0, 0, 0.6)' : 'none'};
  opacity: ${(isShowing ? '1' : '0')};
  transform: ${isShowing
    ? 'translateX(0)'
    : `translateX(${105 * (side === 'right' ? 1 : -1)}%)`};
  transition: transform .15s cubic-bezier(.25, .46, .45, .94), opacity .15s ease-out;
  pointer-events: ${isShowing ? 'auto' : 'none'};
  ${(side === 'right' ? 'right: 0;' : 'left: 0;')};
  max-width: ${`${maxWidth} !important` || 'inherit'};

  ${fullScreenOnMobile
    ? css` ${up('tablet')} {
      width: 80vw;
    }`
    : ''};

  ${smoothScroll} ${up('desktop')} {
    max-width: 600px;
  }

  .sidepane__wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100%;
    overflow: hidden;
  }
`)

const BackButton = styled.button(() => `
  ${resetButton};
  position: absolute;
  left: 10px;
  top: 5px;
  height: 50px;
  line-height: 1.5;

  ${StyledSVGIcon} {
    display: inline-block;
    transform: rotate(180deg);
    vertical-align: middle;
    margin-right: .5em;
  }
`)

const CloseButton = styled.button(({ theme }) => `
  ${resetButton}
  position: absolute;
  left: 1rem;
  top: 1.5rem;
  font-size: 2rem;
  line-height: 1rem;
  transition: transform .3s ease-out;

  &:hover {
    color: ${theme.colors.greyDark};
    transform: scale(1.2);
    transition-duration: .05s;
  }

  // A nice, fat click target for nice, fat fingers.
  &:before {
    position: absolute;
    top: 50%;
    content: "";
    // If this was 50% it'd make more sense, but that puts it too near the edge, so we gotta move it, ya know?
    left: 10%;
    transform: translate(-50%, -50%);
    width: 45px;
    height: 45px;
  }
`)

const OuterContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`

const TitleWrapper = styled.div<Pick<SidePaneProps, 'showCloseButton'>>(({
  showCloseButton,
  theme,
}) => `
  border-bottom : 1px solid ${theme.colors.greyLighter};
  display: flex;
  padding: 20px;
  // Small haxx0r to get it out of the way of the close button thingie
  ${showCloseButton ? 'padding-right: 40px' : ''};

  &, * {
    font-family: ${theme.typography.fontFamily};
    font-weight: bold;
    text-transform: uppercase;
    min-height: 60px;
  }
`)

const ContentWrapper = styled.div<{ hasTitle?: boolean }>`
  overflow: auto;
  flex: auto;
  padding: ${({ hasTitle }) => (hasTitle ? '20px 0' : '0 0 20px')};
  position: relative;
`

const SidepaneFooter = styled.footer`
  border-top: 1px solid ${({ theme }) => theme.colors.greyLighter};
  padding: 20px 0;

  button:last-child {
    margin-bottom: 0;
  }
`

export const SidePane: FC<SidePaneProps> = (
  {
    children,
    isShowing,
    fullScreenOnMobile,
    onClose = noop,
    useBackdrop = true,
    side,
    footerContent,
    title,
    showCloseButton,
    maxWidth,
  }: SidePaneProps,
) => {
  const isTablet = useMediaQuery(`(min-width: ${appTheme.breakpoints.tablet})`)
  // We'll want to close the sidepane when Escape is pressed.
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement> | KeyboardEvent) => {
    e.preventDefault()
    onClose()
  }, [onClose])
  useKeyPressEvent('Escape', handleKeyDown)

  // First, we need something to happen only when the "isShowing" setting changes (but
  // not per rendering, because that would be bad).
  useEffect(() => {
    if (isShowing) {
      preventBodyScroll()
      return enableBodyScroll
    }

    enableBodyScroll()
    return noop
  }, [isShowing])

  const { t: translate } = useTranslation()

  const showTitle = title !== undefined || showCloseButton

  useEffect(() => {
    if (window && isShowing && showCloseButton && onClose && !document.URL.endsWith('#')) {
      window.history.pushState(null, '', `${document.URL}#`)
      window.addEventListener('popstate', (event) => {
        event.stopImmediatePropagation()
        event.stopPropagation()
        event.preventDefault()
        if (onClose) {
          onClose()
        }
      }, { once: true })
    } else if (window && !isShowing && document.URL.endsWith('#')) {
      window.history.back()
    }
  }, [isShowing, onClose, showCloseButton])
  return (
    <>
      {useBackdrop && <Backdrop isShowing={isShowing} onBackdropClick={onClose} />}
      <SidePaneWrapper
        aria-hidden={!isShowing}
        className={clsx(isShowing && 'is-active')}
        fullScreenOnMobile={fullScreenOnMobile}
        // Setting "hidden" normally hides the element, but for some reason it still shows up?
        // No matter. As long as we set this and aria-hidden, it'll be as inconspicuous as it
        // can be. If the animation stops working later, we might need to remove this property.
        hidden={!isShowing}
        isShowing={isShowing}
        maxWidth={maxWidth}
        side={side}
      >
        <ReactFocusLock className="sidepane__wrapper" disabled={!isShowing}>

          {showCloseButton && onClose && (!fullScreenOnMobile || isTablet) && (
            <CloseButton
              aria-label={translate('common:CLOSE')}
              onClick={onClose}
              title={translate('common:CLOSE')}
              type="button"
            >
              <SVGIcon color="greyLight" icon={SVGClose} size="14px" />
            </CloseButton>
          )}
          {showCloseButton && onClose && (fullScreenOnMobile && !isTablet) && (
            <BackButton
              onClick={onClose}
              type="button"
            >
              <SVGIcon icon={SVGAngleRight} size="16px" />
              {translate('cms:BACK_LABEL')}
            </BackButton>
          )}
          <OuterContentWrapper role="region">
            {showTitle && <TitleWrapper showCloseButton={showCloseButton}>{title}</TitleWrapper>}
            <ContentWrapper hasTitle={showTitle}>
              {children}
            </ContentWrapper>
          </OuterContentWrapper>
          {footerContent && <SidepaneFooter>{footerContent}</SidepaneFooter>}
        </ReactFocusLock>
      </SidePaneWrapper>
    </>
  )
}
